<template>
    <div>
        <kgr-input-group
            :label="`${$t('Sortieren')}:`"
            inline
        >
            <kgr-generic-select
                display="label"
                :model-value="sort"
                :options="[{ value: 'name', label: $t('Name') }, { value: 'created', label: $t('Datum') }]"
                @update:model-value="sort = $event"
            />
        </kgr-input-group>
        <kgr-loading v-if="loading" />
        <table
            v-else-if="files.length > 0"
            class="kgr-table kgr-table--striped kgr-table--block table-groups"
        >
            <thead>
                <th class="kgr-table__cell kgr-table__cell--headline" v-t="$t('Dokument')"></th>
                <th class="kgr-table__cell kgr-table__cell--headline" v-t="$t('Erstellt am')"></th>
                <th class="kgr-table__cell kgr-table__cell--headline" v-t="$t('Stichtag')"></th>
                <th class="kgr-table__cell kgr-table__cell--headline" v-t="$t('E-Signatur')"></th>
                <th
                    class="kgr-table__cell kgr-table__cell--headline kgr-table__cell--actions"
                    v-t="$t('Aktion')"
                    style="width: auto;"
                ></th>
            </thead>
            <tbody>
                <tr
                    v-for="file in paginatedFiles"
                    :id="`letter${file.id}`"
                    :key="file.id"
                    class="kgr-table__row"
                >
                    <td class="kgr-table__cell">
                        {{ file.title }}
                        <kgr-publication-icon :clearing="file.clearing" />
                    </td>
                    <td class="kgr-table__cell">
                        {{ file.created.toLocaleString() }}
                    </td>
                    <td class="kgr-table__cell">
                        {{ (file.referenceDate ?? file.created).toLocaleDateString() }}
                    </td>
                    <td class="kgr-table__cell kgr-table__cell--actions">
                        <template v-if="file.hasSignatures">
                            <kgr-letter-signatures
                                :letter="file.getSignableLetter()"
                                style="display: inline-block"
                                @saved="loadLetter(file.id)"
                            />
                            <kgr-letter-signatures-status
                                view="employee"
                                :signatures="file.getSignableLetter().signatures.filter(c => c.isSignableByEmployee())"
                            />
                            <kgr-letter-signatures-status
                                view="custody"
                                :signatures="file.getSignableLetter().signatures.filter(c => c.isSignableByCustody())"
                                :num-available-signees="file.getSignableLetter().numCustodians"
                            />
                        </template>
                    </td>
                    <td class="kgr-table__cell kgr-table__cell--actions" style="width: auto">
                        <a
                            :href="`/backend/child/${childId}/letters/${file.id}`"
                            :title="$t('file.browser.download')"
                            class="kgr-table__action"
                            target="_blank"
                        >
                            <kgr-icon-download />
                        </a>

                        <a
                            :href="`/backend/child/${childId}/letters/${file.id}/preview`"
                            :title="$t('file.browser.view')"
                            class="kgr-table__action"
                            target="_blank"
                        >
                            <kgr-icon-preview />
                        </a>

                        <kgr-child-letter-send-email
                            :letter="file"
                            class="kgr-table__action"
                            @file-visibility="file.inExchangeDirectory = $event"
                            @email-sent="loadLetter(file.id)"
                        />

                        <kgr-file-visibility-toggle
                            :file-id="file.id"
                            :show="file.inExchangeDirectory"
                            small
                            class="kgr-table__action"
                            @change="file.inExchangeDirectory = $event"
                        />
                        <button
                            :disabled="isDeleteDisabled(file)"
                            :title="deleteTitle(file)"
                            class="kgr-table__action"
                            @click="openDeleteConfirmModal(file)"
                        >
                            <kgr-icon icon-class="delete" :disabled="isDeleteDisabled(file)" />
                        </button>
                    </td>
                </tr>
            </tbody>
        </table>
        <kgr-empty-data
            v-else
            :value="$t('Es wurden noch keine Dokumente erstellt.')"
        />
        <div
            v-if="files.length > paginator.limit"
            style="text-align: center;padding: 20px 0"
        >
            <kgr-paginator
                v-model="paginator"
                :length="files.length"
                :show-limit-options="false"
                style="display: inline-block"
            />
        </div>
        <kgr-generic-modal
            :value="showDeleteModal"
            :headline="$t('warning')"
            @close="showDeleteModal = false"
        >
            {{ $t('Soll dieses Dokument wirklich gelöscht werden?') }}
            <template #footer>
                <kgr-button
                    :value="$t('cancel')"
                    class="kgr-button--outline"
                    @click="showDeleteModal = false"
                />
                <kgr-button
                    :value="$t('delete')"
                    @click="deleteLetter()"
                />
            </template>
        </kgr-generic-modal>
    </div>
</template>

<script>
import ChildLetter from '../../../js/dto/letter/ChildLetter.js';
import ChildLetterSendEmail from './ChildLetterSendEmail.vue';
import EmptyData from '../../common/EmptyData.vue';
import FileVisibilityToggle from '../../exchange-directory/FileVisibilityToggle.vue';
import GenericModal from '../../common/modal/GenericModal.vue';
import GenericSelect from '../../common/form/select/GenericSelect.vue';
import Icon from '../../common/icon/Icon.vue';
import IconDownload from '../../common/icon/IconDownload.vue';
import IconPreview from '../../common/icon/IconPreview.vue';
import InputGroup from '../../common/form/InputGroup.vue';
import KgrButton from '../../common/form/button/KgrButton.vue';
import LetterSignatures from '../signable-letter/LetterSignatures.vue';
import LetterSignaturesStatus from '../signable-letter/LetterSignaturesStatus.vue';
import Loading from '../../common/Loading.vue';
import Paginator from '../../common/Paginator.vue';
import PublicationIcon from '../../common/icon/PublicationIcon.vue';
import axios from 'axios';

export default {
    components: {
        'kgr-file-visibility-toggle': FileVisibilityToggle,
        'kgr-loading': Loading,
        'kgr-empty-data': EmptyData,
        'kgr-paginator': Paginator,
        'kgr-icon': Icon,
        'kgr-generic-modal': GenericModal,
        'kgr-button': KgrButton,
        'kgr-icon-download': IconDownload,
        'kgr-icon-preview': IconPreview,
        'kgr-letter-signatures': LetterSignatures,
        'kgr-letter-signatures-status': LetterSignaturesStatus,
        'kgr-child-letter-send-email': ChildLetterSendEmail,
        'kgr-publication-icon': PublicationIcon,
        'kgr-generic-select': GenericSelect,
        'kgr-input-group': InputGroup,
    },
    props: {
        childId: {
            type: Number,
            required: true,
        },
        // Country managers may delete a letter that was already emailed; customers may not. The API enforces
        // this too — the prop only decides whether the button offers the action.
        canDeleteSentLetters: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            sort: null,
            fileToDelete: null,
            showDeleteModal: false,
            files: [],
            loading: true,
            paginator: {
                limit: 20,
                offset: 0,
            },
        };
    },
    computed: {
        paginatedFiles() {
            const files = this.files;

            if (this.sort?.value === 'name') {
                files.sort((a, b) => a.title.localeCompare(b.title));
            } else if (this.sort?.value === 'created') {
                files.sort((a, b) => b.created - a.created);
            }

            return files.slice(this.paginator.offset, this.paginator.offset + this.paginator.limit);
        },
    },
    mounted() {
        this.loadLetters();
    },
    methods: {
        loadLetter(letterId) {
            axios.get(`/childletters/${letterId}`)
                .then((response) => {
                    this.$set(this.files, this.files.findIndex((f) => f.id === letterId), ChildLetter.fromData(response.data.data));
                })
                .catch((error) => {
                    console.error('Error loading letter:', error);
                });
        },
        isDeleteDisabled(file) {
            return file.sent !== null && !this.canDeleteSentLetters;
        },
        deleteTitle(file) {
            if (!file.sent) {
                return this.$t('delete');
            }

            const params = {
                sent: file.sent.toLocaleDateString(),
                firstname: file.sentBy ? file.sentBy.firstname : 'n/a',
                lastname: file.sentBy ? file.sentBy.lastname : '',
            };

            if (this.canDeleteSentLetters) {
                return this.$t('Dieses Dokument wurde bereits am {sent} (erstmalig) von {firstname} {lastname} per E-Mail verschickt. Kunden können es deshalb nicht löschen — als Country Manager können Sie es trotzdem löschen.', params);
            }

            return this.$t('Dieses Dokument kann nicht gelöscht werden, weil es bereits am {sent} (erstmalig) von {firstname} {lastname} per E-Mail verschickt wurde.', params);
        },
        openDeleteConfirmModal(file) {
            if (this.isDeleteDisabled(file)) {
                return;
            }
            this.fileToDelete = file;
            this.showDeleteModal = true;
        },
        deleteLetter() {
            axios.delete(`/letters/${this.fileToDelete.id}`)
                .then(() => {
                    this.files = this.files.filter((file) => file.id !== this.fileToDelete.id);
                })
                .catch(() => {
                    this.$notify.error('Error deleting letter');
                })
                .finally(() => {
                    this.fileToDelete = null;
                    this.showDeleteModal = false;
                });
        },
        loadLetters() {
            this.loading = true;
            axios.get(`/children/${this.childId}/letters`).then((response) => {
                this.files = response.data.data.map((data) => ChildLetter.fromData(data));
                this.loading = false;
            }).catch(() => {
                this.$notify.error('Error loading letters');
            });
        },

    },
};
</script>

<style scoped lang="scss" rel="stylesheet/scss">
</style>
